import { MembershipPauseSummary } from '@/features/membershipPause/types';

export type SharedMembershipFeatureFlags = {
    read: boolean;
    createPool: boolean;
    mutateParticipants: boolean;
    staffBooking: boolean;
    memberSelfBooking: boolean;
};

const DISABLED_SHARED_MEMBERSHIP_FLAGS: SharedMembershipFeatureFlags = {
    read: false,
    createPool: false,
    mutateParticipants: false,
    staffBooking: false,
    memberSelfBooking: false,
};

export const normalizeSharedMembershipFeatureFlags = (
    value: unknown,
): SharedMembershipFeatureFlags => {
    if (!value || typeof value !== 'object') {
        return { ...DISABLED_SHARED_MEMBERSHIP_FLAGS };
    }

    const flags = value as Partial<SharedMembershipFeatureFlags>;
    return {
        read: flags.read === true,
        createPool: flags.createPool === true,
        mutateParticipants: flags.mutateParticipants === true,
        staffBooking: flags.staffBooking === true,
        memberSelfBooking: flags.memberSelfBooking === true,
    };
};

export const shouldFetchSharedMembershipPool = (
    readEnabled: boolean,
    membershipId: string,
) => readEnabled === true && membershipId.trim().length > 0;

/**
 * 공유 풀을 어떤 원본에서 찾을지 나타낸다.
 * 수강권 상세 화면은 풀 ID를 모르므로 양쪽 다 소스 ID로 바로 조회한다.
 */
export type SharePoolScope =
    | { sourceType: 'MEMBERSHIP'; membershipId: string }
    | { sourceType: 'MEMBER_TICKET'; memberTicketId: string };

export const getSharePoolScopeSourceId = (scope: SharePoolScope) =>
    scope.sourceType === 'MEMBERSHIP' ? scope.membershipId : scope.memberTicketId;

export const shouldFetchSharePoolByScope = (readEnabled: boolean, scope: SharePoolScope) =>
    readEnabled === true && getSharePoolScopeSourceId(scope).trim().length > 0;

export type SharedMembershipParticipantSummary = {
    memberId: string;
    name: string;
    appAccountStatus?: 'linked' | 'withdrawn' | 'unlinked';
    isDeleted?: boolean;
    usage?: {
        usedCount: number;
        futureScheduledCount: number;
        pendingRequestCount: number;
        reservedCount: number;
    };
};

export type SharedMembershipEventAction =
    | 'POOL_CREATED'
    | 'POOL_CREATED_FROM_EXISTING'
    | 'PARTICIPANT_ADDED'
    | 'PARTICIPANT_REMOVED'
    | 'MAX_PARTICIPANTS_CHANGED'
    | 'MAX_LIFETIME_PARTICIPANTS_CHANGED'
    | 'POOL_CLOSED'
    | 'OWNER_WITHDRAWAL_REVIEW_REQUIRED'
    | 'MEMBER_ID_MERGED';

export type SharedMembershipEvent = {
    action: SharedMembershipEventAction;
    targetMemberId?: string;
    targetMemberName?: string;
    beforeMaxParticipants?: number;
    afterMaxParticipants?: number;
    beforeMaxLifetimeParticipants?: number;
    afterMaxLifetimeParticipants?: number;
    reason?: string;
    actor?: { userId?: string; role?: string; name?: string };
    createdAt?: string;
};

export type SharedMembershipUsageSummary = {
    capacity?: number;
    usedCount?: number;
    futureScheduledCount?: number;
    pendingRequestCount?: number;
    usageRemaining?: number;
    reservedCount?: number;
    bookableRemaining?: number;
};

export type MembershipEntitlementUsage = {
    membershipId: string;
    asOf: string;
    capacity: number;
    usedCount: number;
    futureScheduledCount: number;
    pendingRequestCount: number;
    usageRemaining: number;
    reservedCount: number;
    bookableRemaining: number;
    myUsedCount: number;
    myFutureScheduledCount: number;
    myPendingRequestCount: number;
};

export type StaffMembershipEntitlement = {
    membership: {
        id: string;
        name: string;
        period: number;
        totalSession: number;
        activationDate: string | null;
        expirationDate: string | null;
        coachId: string;
        centerId: string | null;
        /** 열린 정지 요약(정지 기능 서버에서만, 열린 정지가 있을 때만). revision 은 없다 — 해제에만 쓴다. */
        pause?: MembershipPauseSummary;
    };
    access:
        | {
              type: 'PERSONAL';
              role: 'OWNER';
          }
        | {
              type: 'SHARED';
              role: 'OWNER' | 'PARTICIPANT';
              sharePoolId: string;
              maxParticipants: number;
              currentParticipants: number;
              maxLifetimeParticipants: number;
              lifetimeParticipants: number;
          };
    usage: MembershipEntitlementUsage;
    /** 관리자·강사 응답에만 포함되며 회원 본인 API에는 노출되지 않는다. */
    sharedOwner?: {
        memberId: string;
        name: string;
    };
};

export type ShareProvisioningView = {
    status: 'PENDING' | 'COMPLETED' | 'FAILED' | 'REVIEW_REQUIRED';
    retryRequired: boolean;
    provisioningId?: string;
    sharePoolId?: string;
    errorCode?: string;
};

export const canRetryTemplateShareProvisioning = (
    provisioning: ShareProvisioningView | null | undefined,
) =>
    provisioning?.retryRequired === true &&
    (provisioning.status === 'PENDING' || provisioning.status === 'FAILED');

export const shouldWarnTemplateShareProvisioning = (
    provisioning: ShareProvisioningView | null | undefined,
): boolean => !!provisioning && provisioning.status !== 'COMPLETED';

/** 공유 수강권 섹션 앵커. 다른 화면에서 이 섹션으로 스크롤할 때 쓴다. */
export const SHARED_MEMBERSHIP_SECTION_ID = 'shared-membership-section';

export type SharedMembershipSourceType = 'MEMBERSHIP' | 'MEMBER_TICKET';

/** PT 원본 요약. 그룹 소스 풀에서는 null로 온다. */
export type SharedMembershipSourceMembership = {
    membershipId: string;
    name: string;
    totalSession: number;
    startSessionValue?: number;
    activationDate?: string;
    expirationDate?: string;
    status?: string;
    isDeleted?: boolean;
    isCanceled?: boolean;
};

/** 그룹 원본 요약. PT 소스 풀에서는 null로 온다. */
export type SharedMembershipSourceMemberTicket = {
    memberTicketId: string;
    name: string;
    startDate?: string;
    expiryDate?: string;
    /** 그룹의 권위 있는 잔여 회차. PT처럼 역산하지 않는다. */
    remainingCount: number;
    totalSession: number | null;
    status?: string;
    isDeleted?: boolean;
    isCanceled?: boolean;
};

export type SharedMembershipPool = {
    id?: string;
    _id?: string;
    /** 레거시 PT 풀 응답에는 없을 수 있어 없으면 MEMBERSHIP으로 읽는다. */
    sourceType?: SharedMembershipSourceType;
    /** sourceType이 MEMBERSHIP일 때만 존재한다. */
    sourceMembershipId?: string;
    /** sourceType이 MEMBER_TICKET일 때만 존재한다. */
    sourceMemberTicketId?: string;
    ownerMemberId: string;
    /** PT 전용. 그룹 풀은 담당 강사가 없어 값이 없다. */
    coachId?: string;
    centerId: string | null;
    participantMemberIds: string[];
    lifetimeParticipantMemberIds: string[];
    maxParticipants: number;
    maxLifetimeParticipants: number;
    /**
     * 재공유로 만든 풀은 `TEMPLATE_RESHARE` 다. 다만 조회 응답(membershipShareView)이
     * `origin` 을 싣지 않아 화면에서는 항상 undefined 다 — 판정 근거로 쓰지 않는다.
     */
    origin: 'TEMPLATE' | 'TEMPLATE_RESHARE' | 'EXISTING_MEMBERSHIP' | 'EXISTING_MEMBER_TICKET';
    status: 'ACTIVE' | 'CLOSED';
    closedAt?: string;
    ownerSummary?: SharedMembershipParticipantSummary;
    participantSummaries?: SharedMembershipParticipantSummary[];
    /** PT만 채워진다. 그룹은 null. */
    usage?: SharedMembershipUsageSummary | null;
    sourceMembership?: SharedMembershipSourceMembership | null;
    sourceMemberTicket?: SharedMembershipSourceMemberTicket | null;
    events?: SharedMembershipEvent[];
    currentParticipantCount?: number;
    lifetimeParticipantCount?: number;
};

export const getSharePoolSourceType = (
    pool: SharedMembershipPool | null | undefined,
): SharedMembershipSourceType =>
    pool?.sourceType === 'MEMBER_TICKET' || (!pool?.sourceType && !!pool?.sourceMemberTicketId)
        ? 'MEMBER_TICKET'
        : 'MEMBERSHIP';

export const isGroupSharePool = (pool: SharedMembershipPool | null | undefined) =>
    getSharePoolSourceType(pool) === 'MEMBER_TICKET';

/**
 * 화면에 표시할 잔여 회차.
 * - PT: usage.bookableRemaining (총량 − 사용 − 미래예약 − 대기신청)
 * - 그룹: sourceMemberTicket.remainingCount
 * 값을 확인할 수 없으면 null을 돌려 화면에서 '-'로 처리한다.
 */
export const getSharePoolRemainingCount = (
    pool: SharedMembershipPool | null | undefined,
): number | null => {
    if (!pool) return null;
    const raw = isGroupSharePool(pool)
        ? pool.sourceMemberTicket?.remainingCount
        : pool.usage?.bookableRemaining;
    return typeof raw === 'number' && Number.isFinite(raw) ? raw : null;
};

/** 원본 수강권 이름. 소스 종류와 무관하게 헤더에 쓴다. */
export const getSharePoolSourceName = (pool: SharedMembershipPool | null | undefined) =>
    (isGroupSharePool(pool) ? pool?.sourceMemberTicket?.name : pool?.sourceMembership?.name) || '';

/**
 * 그룹은 회차를 사람별로 나눠 집계하지 않아 참여자 usage가 전부 0으로 온다.
 * 0을 "한 번도 안 썼다"로 오해하지 않도록 그룹에서는 사용량 문구를 감춘다.
 */
export const shouldShowParticipantUsage = (pool: SharedMembershipPool | null | undefined) =>
    !isGroupSharePool(pool);

export type MembershipTemplateSharePolicy = {
    enabled: boolean;
    maxParticipants: number | null;
    maxLifetimeParticipants: number | null;
};

/**
 * 공유 회차는 사람 수만큼 빠진다.
 *
 * 예전에는 "참여자끼리 같은 시간에 함께 수업할 수 없다"고 안내했는데, 그건 제품 규칙이
 * 아니라 1:2 묶음 예약이 서버에 없어서 생긴 제약이었다. 이제 1:2도 공유 회차로 등록되므로
 * 남은 건 "두 명이 함께 들으면 2회가 빠진다"는 사실 하나다. 이걸 모르면 회차 분쟁이 난다.
 */
export const MULTI_WRITE_NOTICE =
    '공유 회차는 수업을 들은 사람 수만큼 차감됩니다. 참여자 2명이 1:2 수업을 함께 들으면 이 수강권에서 2회가 빠집니다.';

/** 발급 즉시 공유 풀이 열리는 상품인지. 카드/모달이 공유 여부를 미리 알리는 데 쓴다. */
export const isSharedMembershipTemplate = (
    template: { sharePolicy?: MembershipTemplateSharePolicy | null } | null | undefined,
) => template?.sharePolicy?.enabled === true;

/**
 * 공유 상품 배지 문구.
 * 인원 한도는 화면에서 다루지 않는 안전값이므로 숫자를 노출하지 않는다.
 */
export const getSharedTemplatePolicyLabel = (
    template: { sharePolicy?: MembershipTemplateSharePolicy | null } | null | undefined,
) => (isSharedMembershipTemplate(template) ? '공유 상품' : '');

export type SharePoolLimits = {
    maxParticipants: number;
    maxLifetimeParticipants: number;
};

/**
 * 공유 풀을 만들 때 항상 보내는 인원 한도.
 *
 * 한도는 센터가 정할 정책이 아니라 남용 방지 안전값이라 화면에서 입력받지 않는다.
 * 다만 백엔드 Joi(`membershipShare.validation.js`)가 두 값을 필수로 받아 생략할 수
 * 없으므로, 서버 전역 상한과 같은 값을 보내 사실상 무제한으로 둔다.
 * 서버 상한(config `sharedMembership.maxParticipants` / `maxLifetimeParticipants`)을
 * 낮추면 이 값도 함께 낮춰야 한다.
 */
export const DEFAULT_SHARE_POOL_LIMITS: SharePoolLimits = {
    maxParticipants: 100,
    maxLifetimeParticipants: 1000,
};

export const getSharePoolId = (pool: SharedMembershipPool | null | undefined) =>
    pool?.id || pool?._id || '';

export const getParticipantSummaryMemberId = (
    participant: SharedMembershipParticipantSummary,
) => participant.memberId;

/**
 * 공유 참여자를 "N명 — 이름 · 이름"으로 요약한다.
 * 인원 수보다 누구와 공유 중인지가 운영에 필요한 정보다.
 * 이름을 확인할 수 없는 참여자는 인원 수에는 포함하되 이름 목록에서는 빠진다.
 */
export const summarizeSharePoolParticipants = (
    pool: SharedMembershipPool | null | undefined,
    fallbackOwnerName = '',
): { count: number; names: string[] } => {
    if (!pool) return { count: 0, names: [] };

    const ownerName = pool.ownerSummary?.name || fallbackOwnerName;
    const names = ownerName ? [`${ownerName}(소유자)`] : [];
    const summaryByMemberId = new Map(
        (pool.participantSummaries || []).map((summary) => [String(summary.memberId), summary]),
    );
    for (const participantId of pool.participantMemberIds || []) {
        const name = summaryByMemberId.get(String(participantId))?.name;
        if (name) names.push(name);
    }

    const count =
        pool.currentParticipantCount ?? 1 + new Set(pool.participantMemberIds || []).size;
    return { count, names };
};

/**
 * 수강권 상세 상단에 한 줄로 보여줄 공유 요약.
 * 소유자를 뺀 상대 이름을 앞세운다 — "누구와 공유 중인지"가 알고 싶은 것이기 때문이다.
 * 활성 공유가 아니거나 상대가 없으면 빈 문자열.
 */
export const buildSharePoolHeadline = (
    pool: SharedMembershipPool | null | undefined,
    fallbackOwnerName = '',
): string => {
    if (pool?.status !== 'ACTIVE') return '';

    const { count, names } = summarizeSharePoolParticipants(pool, fallbackOwnerName);
    const otherNames = names.filter((name) => !name.endsWith('(소유자)'));
    const others = Math.max(0, count - 1);
    if (others === 0) return '참여 회원이 아직 없습니다';
    if (otherNames.length === 0) return `${others}명과 공유 중`;
    return otherNames.length === 1
        ? `${otherNames[0]}님과 공유 중`
        : `${otherNames[0]}님 외 ${others - 1}명과 공유 중`;
};

/** 이 회원이 "공유받아" 쓰는 수강권만 고른다. 소유자 본인 것과 개인 수강권은 뺀다. */
/**
 * 그룹 공유 사용 권리. PT와 회차 모델이 달라 서버가 별도 배열로 내려준다
 * (memberTicket + access). 참여자 필터링 규칙은 PT와 동일하다.
 */
export type StaffGroupEntitlement = {
    memberTicket: {
        id: string;
        name: string;
        centerId: string;
        startDate: string | null;
        expiryDate: string | null;
        remainingCount: number;
        totalSession: number | null;
        status: string;
    };
    access:
        | { type: 'PERSONAL'; role: 'OWNER' }
        | {
              type: 'SHARED';
              role: 'OWNER' | 'PARTICIPANT';
              sharePoolId: string;
              ownerMemberId: string;
              ownerName: string;
              maxParticipants: number;
              currentParticipants: number;
          };
};

export const selectSharedParticipantGroupEntitlements = (
    entitlements: StaffGroupEntitlement[] | null | undefined,
): StaffGroupEntitlement[] =>
    (entitlements || []).filter(
        (entitlement) =>
            entitlement?.access?.type === 'SHARED' && entitlement.access.role === 'PARTICIPANT',
    );

export const selectSharedParticipantEntitlements = (
    entitlements: StaffMembershipEntitlement[] | null | undefined,
): StaffMembershipEntitlement[] =>
    (entitlements || []).filter(
        (entitlement) =>
            entitlement?.access?.type === 'SHARED' && entitlement.access.role === 'PARTICIPANT',
    );
